import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Tabs, Tab, Table, Button, Form, Alert, Spinner, Badge, InputGroup, FormControl, Pagination } from 'react-bootstrap';
import { adminAPI } from '../../services/apiService';
import { message } from 'antd';
import { SearchOutlined, EyeOutlined, UserAddOutlined, DollarOutlined, DeleteOutlined, BarChartOutlined, WifiOutlined, DashboardOutlined, BellOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { getTranslatedError, parseApiValidationError } from '../../utils/errorTranslator';
import { debounce } from '../../utils/debounce';
import Avatar from '../common/Avatar';
import Statistics from './Statistics';
import WebSocketClients from './WebSocketClients';
// import SystemMonitoring from './SystemMonitoring'; // для вкладки Мониторинг (временно скрыта)
import BalanceManagement from './BalanceManagement';
import Notifications from './Notifications';
import TrainingManagement from './TrainingManagement';
import UserDetails from './UserDetails';
import StackedModal from '../common/StackedModal';

const Modal = StackedModal;

const AdminPanel = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  
  // Фильтры и пагинация
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    isActive: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Формы
  const [createForm, setCreateForm] = useState({
    login: '',
    password: '',
    phone: '',
    email: '',
    firstName: '',
    lastName: '',
    nickname: '',
    role: 'user',
    isActive: true,
  });
  const [createFormErrors, setCreateFormErrors] = useState({});

  const filtersRef = useRef(filters);
  const paginationRef = useRef(pagination);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const loadUsers = useCallback(async (pageOverride = null) => {
    try {
      setLoading(true);
      setError('');
      const currentFilters = filtersRef.current;
      const currentPagination = paginationRef.current;
      const params = {
        page: pageOverride !== null ? pageOverride : currentPagination.page,
        limit: currentPagination.limit,
        ...currentFilters,
      };
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const data = await adminAPI.getUsers(params);
      setUsers(Array.isArray(data.users) ? data.users : []);
      if (data.pagination) {
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total || 0,
          totalPages: data.pagination.pages || 0,
        }));
      }
    } catch (err) {
      setError('Ошибка при загрузке пользователей: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedLoadUsers = useMemo(
    () => debounce((page) => loadUsers(page), 500),
    [loadUsers]
  );

  useEffect(() => {
    return () => debouncedLoadUsers.cancel?.();
  }, [debouncedLoadUsers]);

  useEffect(() => {
    if (filters.search) {
      debouncedLoadUsers(pagination.page);
    } else {
      loadUsers();
    }
  }, [pagination.page, filters.role, filters.isActive, filters.search, loadUsers, debouncedLoadUsers]);

  const applySearchNow = () => {
    debouncedLoadUsers.cancel?.();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadUsers(1);
  };

  const handleViewUser = async (userId) => {
    try {
      const userData = await adminAPI.getUserDetails(userId);
      setSelectedUser(userData);
    } catch (err) {
      message.error('Ошибка при загрузке данных пользователя: ' + getTranslatedError(err));
    }
  };

  const updateCreateForm = (field, value) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
    setCreateFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const resetCreateForm = () => {
    setCreateForm({
      login: '',
      password: '',
      phone: '',
      email: '',
      firstName: '',
      lastName: '',
      nickname: '',
      role: 'user',
      isActive: true,
    });
    setCreateFormErrors({});
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateFormErrors({});
    if (createForm.email && !isValidEmail(createForm.email)) {
      const emailError = 'Неверный формат email';
      setCreateFormErrors({ email: emailError });
      message.error(emailError);
      return;
    }
    if (createForm.phone && !isValidPhone(createForm.phone)) {
      const phoneError = 'Введите телефон в формате +7 (XXX) XXX-XX-XX';
      setCreateFormErrors({ phone: phoneError });
      message.error(phoneError);
      return;
    }
    if (!createForm.nickname.trim()) {
      const nicknameError = 'Укажите никнейм';
      setCreateFormErrors({ nickname: nicknameError });
      message.error(nicknameError);
      return;
    }
    try {
      await adminAPI.createUser({
        ...createForm,
        nickname: createForm.nickname.trim(),
      });
      message.success('Пользователь успешно создан');
      setShowCreateModal(false);
      resetCreateForm();
      loadUsers();
    } catch (err) {
      const { message: errorMessage, fieldErrors } = parseApiValidationError(
        err,
        'Ошибка при создании пользователя'
      );
      if (Object.keys(fieldErrors).length > 0) {
        setCreateFormErrors(fieldErrors);
      }
      message.error('Ошибка при создании пользователя: ' + errorMessage);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      const response = await adminAPI.deleteUser(userToDelete.id);
      message.success(`Пользователь ${response.deletedUser?.login || userToDelete.login} успешно удален`);
      
      // Если удален текущий выбранный пользователь, очищаем выбор
      if (selectedUser && (selectedUser.user?.id === userToDelete.id || selectedUser.id === userToDelete.id)) {
        setSelectedUser(null);
      }
      
      setShowDeleteModal(false);
      setUserToDelete(null);
      
      // Обновляем список пользователей
      loadUsers();
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.code === 'CANNOT_DELETE_SELF') {
        message.error('Нельзя удалить свой собственный аккаунт');
      } else if (err.response?.status === 409) {
        message.error('Нельзя удалить пользователя: есть связанные данные (задачи или заявки)');
      } else {
        message.error('Ошибка при удалении пользователя: ' + getTranslatedError(err));
      }
    }
  };

  const handleDeleteClick = (userToDelete) => {
    // Проверяем, не пытаемся ли удалить себя
    if (userToDelete.id === user?.id) {
      message.warning('Нельзя удалить свой собственный аккаунт');
      return;
    }
    setUserToDelete(userToDelete);
    setShowDeleteModal(true);
  };

  const handleUserUpdated = async () => {
    loadUsers();
    if (selectedUser) {
      try {
        const userId = selectedUser.user?.id || selectedUser.id;
        const updated = await adminAPI.getUserDetails(userId);
        setSelectedUser(updated);
      } catch (e) {
        console.error('Error updating selected user:', e);
      }
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Активен</Badge>
    ) : (
      <Badge bg="danger">Неактивен</Badge>
    );
  };

  const getRoleBadge = (role) => {
    return role === 'admin' ? (
      <Badge bg="danger">Admin</Badge>
    ) : (
      <Badge bg="info">User</Badge>
    );
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits.length) return '';
    const normalized = digits.startsWith('8') || digits.startsWith('7') ? digits.slice(1) : digits;
    const cut = normalized.slice(0, 10);
    const part1 = cut.slice(0, 3);
    const part2 = cut.slice(3, 6);
    const part3 = cut.slice(6, 8);
    const part4 = cut.slice(8, 10);
    let formatted = '+7';
    if (part1) formatted += ` (${part1}`;
    if (part1 && part1.length === 3) formatted += ')';
    if (part2) formatted += ` ${part2}`;
    if (part3) formatted += `-${part3}`;
    if (part4) formatted += `-${part4}`;
    return formatted;
  };

  const isValidPhone = (value) => /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/.test(value);
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col xl={10} lg={11}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title className="mb-4">Админ-панель</Card.Title>
              
              {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
              
              <Tabs 
                activeKey={activeTab} 
                onSelect={(k) => setActiveTab(k)} 
                className="mb-3"
              >
                <Tab 
                  eventKey="list" 
                  title={
                    <span className="d-flex align-items-center gap-2">
                      <UserAddOutlined />
                      Список пользователей
                    </span>
                  }
                >
                  {selectedUser ? (
                    <UserDetails
                      user={selectedUser}
                      currentUser={user}
                      onBack={() => setSelectedUser(null)}
                      onUserUpdated={handleUserUpdated}
                      onDelete={(u) => handleDeleteClick(u)}
                    />
                  ) : (
                    <div className="mb-3">
                      <Row>
                        <Col md={4} className="mb-3">
                          <InputGroup>
                            <FormControl
                              placeholder="Поиск..."
                              value={filters.search}
                              onChange={(e) => {
                                setFilters({ ...filters, search: e.target.value });
                                setPagination(prev => ({ ...prev, page: 1 }));
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  applySearchNow();
                                }
                              }}
                            />
                            <Button variant="outline-secondary" onClick={applySearchNow}>
                              <SearchOutlined />
                            </Button>
                          </InputGroup>
                        </Col>
                        <Col md={3} className="mb-3">
                          <Form.Select
                            value={filters.role}
                            onChange={(e) => {
                              setFilters({ ...filters, role: e.target.value });
                              setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                          >
                            <option value="">Все роли</option>
                            <option value="admin">Admin</option>
                            <option value="user">User</option>
                          </Form.Select>
                        </Col>
                        <Col md={3} className="mb-3">
                          <Form.Select
                            value={filters.isActive}
                            onChange={(e) => {
                              setFilters({ ...filters, isActive: e.target.value });
                              setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                          >
                            <option value="">Все статусы</option>
                            <option value="true">Активные</option>
                            <option value="false">Неактивные</option>
                          </Form.Select>
                        </Col>
                        <Col md={2} className="mb-3">
                          <Button variant="primary" onClick={() => setShowCreateModal(true)} className="w-100">
                            <UserAddOutlined /> Создать
                          </Button>
                        </Col>
                      </Row>

                      {loading ? (
                        <div className="text-center py-5">
                          <Spinner animation="border" variant="primary" />
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive">
                            <Table striped bordered hover>
                              <thead>
                                <tr>
                                  <th style={{ width: '50px' }}>Аватар</th>
                                  <th>Логин</th>
                                  <th>Имя</th>
                                  <th>Email</th>
                                  <th>Телефон</th>
                                  <th>Баланс</th>
                                  <th>Роль</th>
                                  <th>Статус</th>
                                  <th>Протоколов</th>
                                  <th>Завершено</th>
                                  <th>Действия</th>
                                </tr>
                              </thead>
                              <tbody>
                                {users.map((u) => (
                                  <tr key={u.id}>
                                    <td>
                                      <Avatar user={u} size={32} showBorder />
                                    </td>
                                    <td>{u.login}</td>
                                    <td>{(u.first_name || u.firstName || '')} {(u.last_name || u.lastName || '')}</td>
                                    <td>{u.email || '—'}</td>
                                    <td>{u.phone || '—'}</td>
                                    <td>{u.requestsBalance || u.requests_balance || 0}</td>
                                    <td>{getRoleBadge(u.role)}</td>
                                    <td>{getStatusBadge(u.is_active !== undefined ? u.is_active : (u.isActive !== undefined ? u.isActive : true))}</td>
                                    <td>{u.statistics?.totalTasks || 0}</td>
                                    <td>{u.statistics?.completedTasks || 0}</td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <Button
                                          variant="outline-info"
                                          size="sm"
                                          onClick={() => handleViewUser(u.id)}
                                          title="Просмотреть детали"
                                        >
                                          <EyeOutlined />
                                        </Button>
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          onClick={() => handleDeleteClick(u)}
                                          title="Удалить пользователя"
                                          disabled={u.id === user?.id}
                                        >
                                          <DeleteOutlined />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>

                          {pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-center mt-3">
                              <Pagination>
                                <Pagination.First
                                  disabled={pagination.page === 1}
                                  onClick={() => setPagination({ ...pagination, page: 1 })}
                                />
                                <Pagination.Prev
                                  disabled={pagination.page === 1}
                                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                                />
                                {[...Array(pagination.totalPages)].map((_, i) => {
                                  const page = i + 1;
                                  if (
                                    page === 1 ||
                                    page === pagination.totalPages ||
                                    (page >= pagination.page - 2 && page <= pagination.page + 2)
                                  ) {
                                    return (
                                      <Pagination.Item
                                        key={page}
                                        active={page === pagination.page}
                                        onClick={() => setPagination({ ...pagination, page })}
                                      >
                                        {page}
                                      </Pagination.Item>
                                    );
                                  }
                                  return null;
                                })}
                                <Pagination.Next
                                  disabled={pagination.page === pagination.totalPages}
                                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                                />
                                <Pagination.Last
                                  disabled={pagination.page === pagination.totalPages}
                                  onClick={() => setPagination({ ...pagination, page: pagination.totalPages })}
                                />
                              </Pagination>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </Tab>

                <Tab 
                  eventKey="statistics" 
                  title={
                    <span className="d-flex align-items-center gap-2">
                      <BarChartOutlined />
                      Статистика
                    </span>
                  }
                >
                  {activeTab === 'statistics' && <Statistics />}
                </Tab>

                <Tab 
                  eventKey="websocket" 
                  title={
                    <span className="d-flex align-items-center gap-2">
                      <WifiOutlined />
                      WebSocket клиенты
                    </span>
                  }
                >
                  {activeTab === 'websocket' && <WebSocketClients />}
                </Tab>

                {/* Вкладка Мониторинг временно скрыта
                <Tab 
                  eventKey="monitoring" 
                  title={
                    <span className="d-flex align-items-center gap-2">
                      <DashboardOutlined />
                      Мониторинг
                    </span>
                  }
                >
                  {activeTab === 'monitoring' && <SystemMonitoring />}
                </Tab>
                */}

                <Tab 
                  eventKey="balance" 
                  title={
                    <span className="d-flex align-items-center gap-2">
                      <DollarOutlined />
                      Балансы
                    </span>
                  }
                >
                  {activeTab === 'balance' && <BalanceManagement />}
                </Tab>

                <Tab 
                  eventKey="notifications" 
                  title={
                    <span className="d-flex align-items-center gap-2">
                      <BellOutlined />
                      Уведомления
                    </span>
                  }
                >
                  {activeTab === 'notifications' && <Notifications />}
                </Tab>

                <Tab 
                  eventKey="training" 
                  title={
                    <span className="d-flex align-items-center gap-2">
                      <PlayCircleOutlined />
                      Обучение
                    </span>
                  }
                >
                  {activeTab === 'training' && <TrainingManagement />}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Модальное окно создания пользователя */}
      <Modal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setCreateFormErrors({});
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Создать нового пользователя</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Логин *</Form.Label>
              <Form.Control
                type="text"
                value={createForm.login}
                onChange={(e) => updateCreateForm('login', e.target.value)}
                isInvalid={!!createFormErrors.login}
                required
                minLength={3}
                maxLength={50}
              />
              <Form.Control.Feedback type="invalid">
                {createFormErrors.login || 'Неверный логин'}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Пароль *</Form.Label>
              <Form.Control
                type="password"
                value={createForm.password}
                onChange={(e) => updateCreateForm('password', e.target.value)}
                isInvalid={!!createFormErrors.password}
                required
                minLength={6}
              />
              <Form.Control.Feedback type="invalid">
                {createFormErrors.password || 'Пароль должен быть не короче 6 символов'}
              </Form.Control.Feedback>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Имя</Form.Label>
                  <Form.Control
                    type="text"
                    value={createForm.firstName}
                    onChange={(e) => updateCreateForm('firstName', e.target.value)}
                    isInvalid={!!createFormErrors.firstName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createFormErrors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Фамилия</Form.Label>
                  <Form.Control
                    type="text"
                    value={createForm.lastName}
                    onChange={(e) => updateCreateForm('lastName', e.target.value)}
                    isInvalid={!!createFormErrors.lastName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createFormErrors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                inputMode="email"
                value={createForm.email}
                onChange={(e) => updateCreateForm('email', e.target.value.replace(/\s/g, ''))}
                isInvalid={
                  !!createFormErrors.email ||
                  (!!createForm.email && !isValidEmail(createForm.email))
                }
                placeholder="user@example.com"
              />
              <Form.Control.Feedback type="invalid">
                {createFormErrors.email || 'Неверный формат email'}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Телефон</Form.Label>
              <Form.Control
                type="tel"
                inputMode="tel"
                value={createForm.phone}
                onChange={(e) => updateCreateForm('phone', formatPhone(e.target.value))}
                isInvalid={
                  !!createFormErrors.phone ||
                  (!!createForm.phone && !isValidPhone(createForm.phone))
                }
                placeholder="+7 (___) ___-__-__"
                maxLength={18}
              />
              <Form.Control.Feedback type="invalid">
                {createFormErrors.phone || 'Формат: +7 (XXX) XXX-XX-XX'}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Никнейм *</Form.Label>
              <Form.Control
                type="text"
                value={createForm.nickname}
                onChange={(e) => updateCreateForm('nickname', e.target.value)}
                isInvalid={!!createFormErrors.nickname}
                required
              />
              <Form.Control.Feedback type="invalid">
                {createFormErrors.nickname || 'Укажите никнейм'}
              </Form.Control.Feedback>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Роль</Form.Label>
                  <Form.Select
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Статус</Form.Label>
                  <Form.Select
                    value={createForm.isActive}
                    onChange={(e) => setCreateForm({ ...createForm, isActive: e.target.value === 'true' })}
                  >
                    <option value="true">Активен</option>
                    <option value="false">Неактивен</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Отмена
            </Button>
            <Button variant="primary" type="submit">
              Создать
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Модальное окно подтверждения удаления */}
      <Modal show={showDeleteModal} onHide={() => {
        setShowDeleteModal(false);
        setUserToDelete(null);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Подтверждение удаления</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userToDelete && (
            <div>
              <Alert variant="warning" className="mb-3">
                <strong>Внимание!</strong> Это действие нельзя отменить.
              </Alert>
              <p>Вы уверены, что хотите удалить пользователя:</p>
              <ul>
                <li><strong>Логин:</strong> {userToDelete.login}</li>
                <li><strong>ID:</strong> {userToDelete.id}</li>
                {userToDelete.email && <li><strong>Email:</strong> {userToDelete.email}</li>}
                {userToDelete.phone && <li><strong>Телефон:</strong> {userToDelete.phone}</li>}
              </ul>
              <p className="text-danger">
                <strong>Все связанные данные (задачи, заявки) будут сохранены, но пользователь будет удален из системы.</strong>
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleDeleteUser}>
            <DeleteOutlined /> Удалить
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminPanel;
